import React, { useMemo, useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { message } from 'antd';
import Results from '../../components/results/results';
import ProjectShell from '../../components/layout/ProjectShell';
import ExportResults from '../../components/exportResults/exportResults';
import {
  Button,
  HelpWindow,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  ZoneButton,
  krPcHelpTopics
} from '../../design-system';
import { XAxisToggle } from '../../design-system/correlation/components';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { useCorrelation } from '../../providers/Correlation';
import { useAutoCorrelationOptional } from '../../providers/AutoCorrelation/context';
import { saveSelectedFziFunctions } from '../../api/data';
import {
  Project,
  SelectedFziFunctions,
  SelectedFziFunctionsOptions
} from '../../@types/projects/types';
import { ReactComponent as EmptyIllustration } from '../../assets/empty-chart-kr-pc-model.svg';
import styles from './KrPcModelPage.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

const KrPcNoZonesContent = ({ onHelpOpen }: { onHelpOpen: () => void }) => (
  <div className={styles.emptyState}>
    <EmptyIllustration style={{ width: '420px' }} />
    <p className={styles.emptyTitle}>No zones created</p>
    <p className={styles.emptySubtitle}>
      Create flow unit zones to compute Kr × Pc Model results.
    </p>
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <ZoneButton />
      <Button variant="secondary" startIcon={<HelpIcon size={14} />} onClick={onHelpOpen}>
        Help
      </Button>
    </div>
  </div>
);

const KrPcNoZonesState = ({ onHelpOpen }: { onHelpOpen: () => void }) => (
  <KrPcNoZonesContent onHelpOpen={onHelpOpen} />
);

const KrPcModelPage = () => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();
  const { selectedXAxis } = useCorrelation();
  const autoCorrelation = useAutoCorrelationOptional();
  const [helpOpen, setHelpOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState('current');
  const project = selectedInfo.project;
  const zones = project?.correlation?.zones;
  const report = autoCorrelation?.report;
  const selectionState = project?.correlation?.selection_state;
  const trackedBaseline =
    selectionState?.auto_baseline_by_x_axis?.[selectedXAxis];
  const baseline =
    trackedBaseline && Object.keys(trackedBaseline).length
      ? trackedBaseline
      : (report?.applied_x_variable ?? report?.x_variable) === selectedXAxis
      ? report?.applied_selection ?? null
      : null;
  const overrides =
    selectionState?.manual_overrides_by_x_axis?.[selectedXAxis] ?? {};
  const overrideCount = Object.keys(overrides).length;
  const provenanceOrigin =
    baseline && overrideCount > 0
      ? 'auto_with_manual_adjustments'
      : baseline
      ? 'auto'
      : selectionState?.origin ?? 'manual';

  const appliedAutoProject = useMemo<Project | null>(() => {
    const snapshot = report?.applied_zone_values;
    const snapshotAxis = report?.applied_x_variable ?? report?.x_variable;
    if (!project || !report?.applied || !snapshotAxis || !snapshot) return null;
    if (!Object.keys(snapshot).length) return null;

    return {
      ...project,
      correlation: project.correlation
        ? {
            ...project.correlation,
            zones: (project.correlation.zones ?? []).map(zone => {
              const zoneValues = snapshot[zone.uid];
              if (!zoneValues) return zone;
              const emptyCurves = {
                liao_curves: null,
                lambda_curves: null
              };
              return {
                ...zone,
                zone_values:
                  snapshotAxis === 'fzi' ? zoneValues : zone.zone_values,
                zone_values_by_x_axis: {
                  ...(zone.zone_values_by_x_axis ?? {}),
                  [snapshotAxis]: zoneValues
                },
                zone_curves:
                  snapshotAxis === 'fzi' ? emptyCurves : zone.zone_curves,
                zone_curves_by_x_axis: {
                  ...(zone.zone_curves_by_x_axis ?? {}),
                  [snapshotAxis]: emptyCurves
                }
              };
            })
          }
        : project.correlation
    };
  }, [project, report]);

  const resetAllToAuto = async () => {
    if (!project?.uid || !baseline) return;
    const selected = Object.fromEntries(
      Object.entries(baseline).filter(
        (entry): entry is [string, SelectedFziFunctionsOptions] =>
          entry[1] != null
      )
    ) as SelectedFziFunctions;
    if (!Object.keys(selected).length) return;

    setResetting(true);
    try {
      const updatedProject = await saveSelectedFziFunctions(project.uid, {
        x_variable: selectedXAxis,
        selected
      });
      setSelectedInfo({ project: updatedProject });
      upsertProject(updatedProject);
      message.success('Manual overrides reset to the applied Auto selection.');
    } catch (error) {
      console.error(error);
      message.error('Could not reset the correlation to Auto.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <ProjectShell>
      {!project ? (
        <div className={styles.emptyState}>
          <EmptyIllustration style={{ width: '420px' }} />
          <p className={styles.emptyTitle}>No project selected</p>
          <p className={styles.emptySubtitle}>
            Select a project from the dashboard to view Kr × Pc Model results.
          </p>
        </div>
      ) : !zones || zones.length === 0 ? (
        <KrPcNoZonesState onHelpOpen={() => setHelpOpen(true)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Single controls line: result tabs lead the row, followed by the
              X-axis and provenance, with the page actions anchored right. */}
          <Tabs
            className={styles.resultTabsRoot}
            value={activeResultTab}
            onChange={setActiveResultTab}
            variant="underline"
            size="md"
          >
            <div className={styles.stickyHeader}>
              <div className={styles.controlsRow}>
                <div className={styles.controlsLeft}>
                  <TabList className={styles.resultTabList}>
                    <Tab value="current">Current Result</Tab>
                    {appliedAutoProject && report ? (
                      <Tab value="auto-baseline">Applied Auto baseline</Tab>
                    ) : null}
                  </TabList>
                  <XAxisToggle />
                  <span className={styles.provenanceBadge}>
                    {provenanceOrigin === 'auto_with_manual_adjustments'
                      ? `Auto + ${overrideCount} manual override${overrideCount === 1 ? '' : 's'}`
                      : provenanceOrigin === 'auto'
                      ? 'Applied Auto'
                      : 'Manual'}
                  </span>
                  {baseline && overrideCount > 0 ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={resetting}
                      onClick={resetAllToAuto}
                    >
                      Reset all to Auto
                    </Button>
                  ) : null}
                </div>
                <div className={styles.controlsRight}>
                  <ExportResults />
                  <Button variant="secondary" startIcon={<HelpIcon size={14} />} onClick={() => setHelpOpen(true)}>
                    Help
                  </Button>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <TabPanels>
                <TabPanel value="current" keepMounted>
                  <Results />
                </TabPanel>
                {appliedAutoProject && report ? (
                  <TabPanel value="auto-baseline" keepMounted>
                    <div>
                      <p className={styles.snapshotNotice}>
                        Read-only snapshot from the last Auto Apply ({(
                          report.applied_x_variable ?? report.x_variable
                        ).toUpperCase()}). Manual changes are shown only in
                        Current Result.
                      </p>
                      <Results
                        projectOverride={appliedAutoProject}
                        xAxisOverride={
                          report.applied_x_variable ?? report.x_variable
                        }
                      />
                    </div>
                  </TabPanel>
                ) : null}
              </TabPanels>
            </div>
          </Tabs>
        </div>
      )}

      <HelpWindow
        topics={krPcHelpTopics}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </ProjectShell>
  );
};

export default KrPcModelPage;
